import { ThemeEvents, PurchaseTypeUpdateEvent } from '@theme/events';
import { planForMonths } from '@theme/utilities';

class ProductPurchaseToggle extends HTMLElement {
  #controller = null;
  #allocations = {};
  #variantId = '';
  #purchaseType = 'subscription';

  connectedCallback() {
    this.#controller = new AbortController();
    const { signal } = this.#controller;

    this.#parseAllocations();
    this.#variantId = this.#getSelectedVariantIdFromSelector() || String(this.dataset.variantId || '');

    for (const radio of this.querySelectorAll('[data-purchase-type]')) {
      radio.addEventListener('change', this.#onPurchaseTypeChange.bind(this), { signal });
    }

    document.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate.bind(this), { signal });

    const defaultType = this.dataset.defaultType === 'one_time' ? 'one_time' : 'subscription';
    this.#purchaseType = this.querySelector('[data-purchase-type]:checked')?.value === 'one_time'
      ? 'one_time'
      : defaultType;

    this.#updateSubscriptionAvailability();
    this.#updatePlanSelect();
    this.#syncForm();
    this.#dispatchUpdate();
  }

  disconnectedCallback() {
    this.#controller?.abort();
  }

  #parseAllocations() {
    const dataEl = this.querySelector('[data-selling-plans]');
    if (!dataEl?.textContent) return;

    try {
      this.#allocations = JSON.parse(dataEl.textContent);
    } catch {
      this.#allocations = {};
    }
  }

  #getForm() {
    const productId = this.dataset.productId;
    if (!productId) return null;

    return document.querySelector(`product-buy-buttons[data-product-id="${productId}"] form`);
  }

  #getPlansForVariant(variantId) {
    return this.#allocations[String(variantId)] || [];
  }

  #getSelectedPlanId() {
    const planInput = this.querySelector('[data-plan-select]');
    return planInput instanceof HTMLInputElement ? planInput.value : '';
  }

  #getMonthsForVariant(variantId) {
    const productId = this.dataset.productId;
    if (!productId) return 0;

    const selector = document.querySelector(`product-supply-selector[data-product-id="${productId}"]`);
    const card = selector?.querySelector(
      `[data-supply-group="subscription"] .product-supply-selector__card[data-variant-id="${variantId}"]`,
    ) || selector?.querySelector(
      `.product-supply-selector__card[data-main-variant-id="${variantId}"]`,
    );
    const months = Number(card?.getAttribute('data-months'));

    return months > 0 ? months : 0;
  }

  #getSelectedVariantIdFromSelector() {
    const productId = this.dataset.productId;
    if (!productId) return '';

    const selector = document.querySelector(`product-supply-selector[data-product-id="${productId}"]`);
    const visibleGroup = selector?.querySelector('[data-supply-group]:not([hidden])') || selector;
    const selected = visibleGroup?.querySelector('.product-supply-selector__input:checked');
    const card = selected instanceof HTMLInputElement
      ? selected.closest('.product-supply-selector__card')
      : null;
    const mainVariantId = card?.getAttribute('data-main-variant-id');

    if (mainVariantId) return mainVariantId;

    return selected instanceof HTMLInputElement ? selected.value : '';
  }

  syncFromVariant(variantId) {
    if (!variantId) return;

    this.#variantId = String(variantId);
    this.#updateSubscriptionAvailability();
    this.#updatePlanSelect();
    this.#dispatchUpdate();
  }

  #syncForm() {
    const form = this.#getForm();
    if (!form) return;

    const input = form.querySelector('[name="selling_plan"]');
    if (!(input instanceof HTMLInputElement)) return;

    const planId = this.#purchaseType === 'subscription' ? this.#getSelectedPlanId() : '';

    if (planId) {
      input.disabled = false;
      input.value = planId;
      return;
    }

    input.value = '';
    input.disabled = true;
  }

  #onPurchaseTypeChange(event) {
    const target = event.target;
    if (!(target instanceof HTMLInputElement)) return;

    this.#purchaseType = target.value === 'one_time' ? 'one_time' : 'subscription';
    this.#updateSubscriptionAvailability();
    this.#updatePlanSelect();
    this.#syncForm();
    this.#dispatchUpdate();
  }

  #onVariantUpdate(event) {
    const productId = this.dataset.productId;
    const detail = event.detail;

    if (productId && detail?.data?.productId && String(detail.data.productId) !== String(productId)) {
      return;
    }

    const variantId = detail?.resource?.id;
    if (!variantId) return;
    if (!(String(variantId) in this.#allocations)) return;

    this.#variantId = String(variantId);
    this.#updateSubscriptionAvailability();
    this.#updatePlanSelect();
    this.#dispatchUpdate();
  }

  #updateSubscriptionAvailability() {
    const subscriptionRadio = this.querySelector('[data-purchase-type="subscription"]');
    if (!(subscriptionRadio instanceof HTMLInputElement)) return;

    const isAvailable = this.#getPlansForVariant(this.#variantId).length > 0;
    subscriptionRadio.disabled = !isAvailable;
    subscriptionRadio.setAttribute('aria-disabled', String(!isAvailable));

    const card = subscriptionRadio.closest('.product-purchase-toggle__card');
    if (card instanceof HTMLElement) {
      card.setAttribute('aria-disabled', String(!isAvailable));
    }
  }

  #updatePlanSelect() {
    const planInput = this.querySelector('[data-plan-select]');
    if (!(planInput instanceof HTMLInputElement)) return;

    const plans = this.#getPlansForVariant(this.#variantId);

    if (this.#purchaseType === 'subscription' && plans.length === 0) {
      const oneTimeRadio = this.querySelector('[data-purchase-type="one_time"]');
      if (oneTimeRadio instanceof HTMLInputElement) {
        oneTimeRadio.checked = true;
      }
      this.#purchaseType = 'one_time';
      planInput.value = '';
    } else if (this.#purchaseType === 'subscription' && plans.length > 0) {
      const months = this.#getMonthsForVariant(this.#variantId);
      const durationPlan = months > 0 ? planForMonths(plans, months) : null;
      planInput.value = String((durationPlan ?? plans[0]).id);
    } else {
      planInput.value = '';
    }

    this.#syncForm();
  }

  #dispatchUpdate() {
    document.dispatchEvent(
      new PurchaseTypeUpdateEvent({
        purchaseType: this.#purchaseType,
        sellingPlanId: this.#purchaseType === 'subscription' ? this.#getSelectedPlanId() : null,
        productId: this.dataset.productId || '',
        variantId: this.#variantId,
        sourceId: this.dataset.blockId || '',
      }),
    );
  }
}

if (!customElements.get('product-purchase-toggle')) {
  customElements.define('product-purchase-toggle', ProductPurchaseToggle);
}
